import { submissionApi } from 'services/apis';
import SubmissionCard from 'components/ui/submision-card';
import { DASHBOARD_SUBMISSION_CARDS_PAGE_SIZE } from 'constants/paginations';
import { SubmissionResponseDtoStatusEnum } from 'libs/pinnacle-sdk';
import React, { useCallback, useMemo, useRef } from 'react';
import { useInfiniteQuery } from 'react-query';

interface SubmissionCardListProps {
  category: SubmissionResponseDtoStatusEnum;
}

const SubmissionCardList: React.FC<SubmissionCardListProps> = ({
  category,
}) => {
  const observer = useRef<IntersectionObserver>();
  const {
    data: submissionList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    'submissions' + category,
    async ({ pageParam = 1 }) =>
      submissionApi
        .filterSubmissions({
          filterSubmissionsQueryDto: {
            includeInsured: true,
            statuses: [category],
          },
          page: pageParam,
          pageSize: DASHBOARD_SUBMISSION_CARDS_PAGE_SIZE,
        })
        .then((res) => res.items),
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.length === DASHBOARD_SUBMISSION_CARDS_PAGE_SIZE
          ? pages.length + 1
          : undefined;
      },
    }
  );

  const lastSubmissionRef = useCallback(
    (node: HTMLDivElement) => {
      if (isFetchingNextPage) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage]
  );

  const submissions = useMemo(() => {
    return submissionList?.pages.flatMap((page) => page) ?? [];
  }, [submissionList]);

  return (
    <div className="overflow-y-auto max-h-[70vh] scrollbar-hide">
      {submissions?.map((submission, index, submissions) => (
        <div
          key={submission.submissionId}
          ref={index === submissions.length - 1 ? lastSubmissionRef : undefined}
          className="mt-[10px]"
        >
          <SubmissionCard {...submission} />
        </div>
      ))}
    </div>
  );
};

export default SubmissionCardList;
